import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, DataTable, EmptyState, FilterBar, FilterChips, Pagination, SelectField, TextField, type Column, fmtBRL } from "@atlas/ui/web";
import { aplicarFiltros, contagens, motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../../lib/recorte";
import { matchAnyKeys } from "../../../lib/text-search";
import { situacaoEncerrada } from "../../../lib/contrato-status";
import { useFiltros } from "../../../lib/use-filtros";
import { atlas } from "../../../lib/sdk";
import { downloadCsv } from "../../../lib/csv";
import { TotalEmFolha, ValorSolicitado } from "../../../components/ValorContrato";
import { totalEmFolha, valorSolicitado } from "@atlas/types";
import type { BancoContratoFull } from "@atlas/sdk";
import { rotuloSubOrganizacao } from "../../../lib/registro-agente";

/** A forma que `ValorSolicitado`/`TotalEmFolha` leem — a mesma da carteira. */
function valoresDe(r: BancoContratoFull) {
  return {
    valorParcela: r.valorParcela,
    totalParcelas: r.totalParcelas,
    valorFinanciado: r.valorFinanciado,
    dadosFinanceirosConhecidos: r.dadosFinanceirosConhecidos,
    origem: r.origem,
    dadosInformados: r.dadosInformados,
  };
}

export function BancoRelatorioConsignacoes() {
  const [tipo, setTipo] = useState<string>("");
  const [inicio, setInicio] = useState<string>("");
  const [fim, setFim] = useState<string>("");
  /** Quem registrou: id da sub organizacao, `principal` ou `nao-registrado`.
   *  Vai para o SERVIDOR, como tipo e periodo — os totais seguem o recorte. */
  const [subOrganizacao, setSubOrganizacao] = useState<string>("");
  const [search, setSearch] = useState("");

  const data = useQuery({
    queryKey: ["banco", "rel", "consig", tipo, inicio, fim, subOrganizacao],
    queryFn: () =>
      atlas.banco.relatorioConsignacoes({
        tipo: tipo || undefined,
        inicio: inicio || undefined,
        fim: fim || undefined,
        subOrganizacao: subOrganizacao || undefined,
      }),
  });

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);

  // SITUACAO COMO ESCOLHA POSITIVA, e a tabela finalmente alcanca o recorte que
  // os indicadores sempre descreveram. Os cards dizem "carteira ativa" desde
  // sempre; a tabela mostrava TUDO, incluindo cancelado — e o hint "total de
  // linhas na tabela pode ser maior" era a tela admitindo a divergencia em vez
  // de resolve-la. Agora "Ativas" e' um clique.
  const [escopo, setEscopo] = useState<"" | "ativas" | "encerradas">("");

  // BUSCA RESTRITA A IDENTIDADE. Antes o alvo incluia `tipoContrato` e
  // `situacao`, duplicando o select de Tipo e o recorte de situacao: digitar
  // "cancelado" trazia linhas pela coluna Situacao, e nao dava para saber se o
  // acerto veio do nome ou do estado.
  const termo = search.trim();
  const filtros = useMemo(() => ({
    escopo: escopo
      ? (l: BancoContratoFull) => (escopo === "encerradas" ? situacaoEncerrada(l.situacao) : !situacaoEncerrada(l.situacao))
      : null,
    busca: termo
      ? (l: BancoContratoFull) => matchAnyKeys(l, termo, ["adf", "matricula", "nome"])
      : null,
  }), [escopo, termo]);

  const todasLinhas = data.data?.linhas ?? [];
  const linhasFiltradas = useMemo(() => aplicarFiltros(todasLinhas, filtros), [todasLinhas, filtros]);
  const semEscopo = useMemo(() => aplicarFiltros(todasLinhas, filtros, "escopo"), [todasLinhas, filtros]);
  const contEscopo = useMemo(
    () => contagens(semEscopo, (l) => (situacaoEncerrada(l.situacao) ? "encerradas" : "ativas")),
    [semEscopo],
  );
  // Trocar o recorte com a posicao antiga cairia num offset que ja nao existe.
  //
  // `tipo`/`inicio`/`fim` FALTAVAM aqui (FR-005). Eles vao para o servidor, e
  // por isso o defeito e' pior que na busca local: a resposta nova pode ter
  // menos linhas que a posicao atual, e o operador estreita o periodo estando
  // na pagina 4 e ve a tabela vazia. Parece relatorio sem dado, e' so' offset
  // alem do fim.
  useEffect(() => { setPagina(1); }, [search, tipo, inicio, fim, subOrganizacao, escopo]);
  const daPagina = recortar(linhasFiltradas, pagina, porPagina);
  const filtrosUi = useFiltros(
    [
      { nome: "busca", ligado: search !== "", limpar: () => setSearch("") },
      { nome: "situacao", ligado: escopo !== "", limpar: () => setEscopo("") },
      { nome: "tipo", ligado: tipo !== "", limpar: () => setTipo("") },
      // Período é UMA dimensão: o operador pensa "de janeiro a março".
      { nome: "periodo", ligado: inicio !== "" || fim !== "", limpar: () => { setInicio(""); setFim(""); } },
      { nome: "subOrganizacao", ligado: subOrganizacao !== "", limpar: () => setSubOrganizacao("") },
    ],
    setPagina,
  );

  const columns: Column<BancoContratoFull>[] = [
    { key: "adf", header: "ADF", mono: true },
    { key: "matricula", header: "Matrícula" },
    { key: "nome", header: "Servidor" },
    { key: "tipoContrato", header: "Tipo" },
    { key: "totalParcelas", header: "Parcelas", align: "right" },
    { key: "valorParcela", header: "Valor parcela", align: "right", render: (r) => fmtBRL(r.valorParcela) },
    // O legado sem informe mostra "—" no principal, nunca R$ 0,00 (feature 012).
    { key: "valorFinanciado", header: "Valor solicitado", align: "right", render: (r) => <ValorSolicitado contrato={valoresDe(r)} formatar={fmtBRL} /> },
    { key: "totalEmFolha", header: "Total em folha", align: "right", render: (r) => <TotalEmFolha contrato={valoresDe(r)} formatar={fmtBRL} /> },
    { key: "origem", header: "Origem", render: (r) => (r.origem === "legado" ? "Legado" : "Atlas") },
    // Quem registrou pelo banco e de qual sub organizacao. Contrato anterior a
    // 01/10/2026 diz "Não registrado" — nao e' a organizacao principal.
    {
      key: "registradoPor",
      header: "Registrado por",
      render: (r) =>
        r.registradoPor ? (
          <span>
            {r.registradoPor.usuarioNome}
            <br />
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{rotuloSubOrganizacao(r.registradoPor)}</span>
          </span>
        ) : (
          <span style={{ color: "var(--text-muted)" }}>{rotuloSubOrganizacao(null)}</span>
        ),
    },
    { key: "situacao", header: "Situação" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Relatórios
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Consignações</h1>
        </div>
        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
          {/* OS DOIS UNIVERSOS, DECLARADOS.
              Estes numeros vem do SERVIDOR e falam da carteira ativa do
              periodo — nao do recorte da tela. Antes o rotulo dizia so'
              "carteira ativa" e o hint admitia que "o total de linhas na
              tabela pode ser maior": duas contagens diferentes lado a lado,
              cada uma de um conjunto, e a tela deixando o operador descobrir
              qual era qual. Agora o card diz de quem fala, e a linha abaixo da
              barra diz quantas linhas o recorte tem. */}
          {/* O legado sem informe nao tem principal: ele entra na quantidade e
              no total em folha, e fica FORA do valor solicitado. Se nenhuma
              operacao viva tem principal conhecido, o card mostra "—" em vez de
              afirmar R$ 0,00. */}
          <Metric
            label="Carteira ativa · base"
            valor={
              !data.data
                ? "—"
                : data.data.quantidade > 0 && data.data.contratosSemDadosFinanceiros >= data.data.quantidade
                  ? "—"
                  : fmtBRL(data.data.totalValorFinanciado)
            }
            hint={
              "Valor solicitado das operações vivas no período consultado. Exclui cancelados, recusados e expirados — e NÃO segue a busca nem os filtros da tabela."
              + (data.data && data.data.contratosSemDadosFinanceiros > 0
                ? ` ${data.data.contratosSemDadosFinanceiros} contrato(s) legado(s) sem valor solicitado informado ficam fora desta soma.`
                : "")
            }
          />
          <Metric
            label="Total em folha · base"
            valor={data.data ? data.data.quantidade > 0 && (data.data.contratosSemCronograma ?? 0) >= data.data.quantidade ? 'Não informado' : fmtBRL(data.data.totalEmFolha) : "—"}
            hint={"Parcela × prazo conhecido das operações vivas." + ((data.data?.contratosSemCronograma ?? 0) > 0 ? ` Soma parcial: ${data.data!.contratosSemCronograma} contrato(s) com cronograma pendente ficam fora.` : '')}
          />
          <Metric
            label="Operações vivas · base"
            valor={String(data.data?.quantidade ?? 0)}
            hint="Quantidade correspondente aos valores ao lado, inclusive legados. Use o chip Situação para ver essas mesmas linhas na tabela."
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              data.data &&
              downloadCsv(
                "consignacoes.csv",
                linhasFiltradas.map((l) => ({
                  adf: l.adf,
                  // Completo: relatorio vai sem censura, no escopo do banco (14/10/2026).
                  cpf: l.cpf || l.cpfMasked,
                  matricula: l.matricula,
                  nome: l.nome,
                  tipo: l.tipoContrato,
                  parcelas: l.cronogramaPendente ? '' : l.totalParcelas,
                  cronograma: l.cronogramaPendente ? 'Pendente' : 'Informado',
                  valorParcela: l.valorParcela,
                  // Vazio, e nao 0, quando o principal nao e' conhecido.
                  valorSolicitado: valorSolicitado(valoresDe(l)) ?? "",
                  totalEmFolha: l.cronogramaPendente ? '' : totalEmFolha(l),
                  origem: l.origem === "legado" ? "Legado" : "Atlas",
                  registradoPor: l.registradoPor?.usuarioNome ?? "",
                  subOrganizacao: rotuloSubOrganizacao(l.registradoPor),
                  situacao: l.situacao,
                })),
              )
            }
          >
            ⬇ Exportar CSV
          </Button>
        </div>
      </header>

      <FilterBar
        searchValue={search}
        onSearchChange={filtrosUi.filtrar(setSearch)}
        searchPlaceholder="Buscar por ADF, matrícula ou nome"
        {...filtrosUi.propsFilterBar}
        actions={
          <FilterChips
            variant="segmentado"
            label="Situação"
            value={escopo}
            onChange={(v) => filtrosUi.filtrar(setEscopo)(v as "" | "ativas" | "encerradas")}
            options={[
              { value: "", label: "Todas", count: semEscopo.length },
              { value: "ativas", label: "Ativas", count: contEscopo.ativas ?? 0 },
              { value: "encerradas", label: "Encerradas", count: contEscopo.encerradas ?? 0 },
            ]}
          />
        }
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
          <SelectField
            label="Tipo"
            value={tipo}
            onChange={(e) => filtrosUi.filtrar(setTipo)(e.target.value)}
            options={[
              { value: "", label: "Todos" },
              { value: "EMPRESTIMO", label: "Empréstimo" },
              { value: "REFIN", label: "Refinanciamento" },
              { value: "ECONSIGNADO", label: "E-Consignado" },
            ]}
          />
          {/* O periodo vai para o SERVIDOR e define o relatorio. O rotulo diz
              QUAL data e' usada: sem isso, "Início 01/09" pode ser lido como
              data do contrato, da averbacao ou do primeiro desconto — tres
              conjuntos diferentes. No legado sem carga detalhada, a data do
              contrato gravada e' a da importacao — o title avisa. */}
          <TextField
            label="Início (lançamento)"
            type="date"
            value={inicio}
            title="Filtra pela data do contrato. No contrato legado sem data informada, é a data em que ele foi importado da folha."
            onChange={(e) => filtrosUi.filtrar(setInicio)(e.target.value)}
          />
          <TextField label="Fim" type="date" value={fim} onChange={(e) => filtrosUi.filtrar(setFim)(e.target.value)} />
          {/* Opcoes vindas do proprio relatorio (so' as que tem contrato), e nao
              do cadastro: este relatorio roda para perfis sem acesso a ele. */}
          <SelectField
            label="Sub organização"
            value={subOrganizacao}
            onChange={(e) => filtrosUi.filtrar(setSubOrganizacao)(e.target.value)}
            options={[{ value: "", label: "Todas" }, ...(data.data?.opcoesSubOrganizacao ?? [])]}
          />
        </div>
      </FilterBar>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(r) => r.adf}
        loading={data.isLoading}
        // `data.isError` nao era checado em lugar nenhum desta tela, e o
        // resultado era o pior caso do FR-013: request quebrado renderizava
        // "Nenhuma consignacao encontrada com os filtros atuais.". O banco lia
        // isso como "nao tenho consignacao neste periodo" — uma afirmacao sobre
        // a carteira dele — quando ninguem tinha conferido nada. Vazio por falha
        // NAO e' vazio; e' ausencia de resposta, e tem de dizer isso.
        emptyState={
          data.isError
            ? "Não foi possível carregar o relatório. Tente novamente."
            : <EmptyState
                motivo={motivoVazio(todasLinhas.length, linhasFiltradas.length)}
                filtroVazio="Nenhuma consignação bate com esses filtros. Amplie o período ou limpe a busca."
                semDados="Nenhuma consignação na carteira ainda."
              />
        }
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="consignações"
      />
    </div>
  );
}

function Metric({ label, valor, hint }: { label: string; valor: string; hint?: string }) {
  return (
    <div title={hint}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: "var(--accent)" }}>{valor}</div>
    </div>
  );
}
